import {
  BillingDates,
  BillingStrategy,
  isFlatRateBillingStrategy,
  OrganizationFeature,
  OrganizationFeatures as OrganizationFeaturesType,
  ProductDesc,
  ProductInterval,
  ProductName,
  ProductShortName,
  ProductSpecMap,
} from './types';
import { FeatureSet } from './feature-set';

export const businessClassFeatures: OrganizationFeaturesType = new Set([
  OrganizationFeature.Export,
  OrganizationFeature.Logo,
  OrganizationFeature.Observers,
  OrganizationFeature.Removal,
  OrganizationFeature.Activity,
  OrganizationFeature.Deactivated,
  OrganizationFeature.OrgCards,
  OrganizationFeature.GoogleApps,
  OrganizationFeature.OrgMembersPage,
  OrganizationFeature.SuperAdmins,
  OrganizationFeature.Prefs,
  OrganizationFeature.InviteBoard,
  OrganizationFeature.InviteOrg,
  OrganizationFeature.RestrictVis,
]);

export const businessClassFeaturesVersion2: OrganizationFeaturesType = new Set([
  ...businessClassFeatures,

  OrganizationFeature.GoldMembers,
  OrganizationFeature.CSVExport,
  OrganizationFeature.ShortExportHistory,
]);

export const businessClassFeaturesVersion3: OrganizationFeaturesType = new Set([
  ...businessClassFeaturesVersion2,

  OrganizationFeature.Tags,
  OrganizationFeature.Plugins,
  OrganizationFeature.AdvancedChecklists,
]);

export const businessClassFeaturesVersion3WithMBG: OrganizationFeaturesType = new Set(
  [...businessClassFeaturesVersion3, OrganizationFeature.ToBeUpgradedToMBGPlan],
);

export const businessClassFeaturesVersion3x3: OrganizationFeaturesType = new Set(
  [...businessClassFeaturesVersion3, OrganizationFeature.MultiBoardGuests],
);

export const enterpriseFeatures: OrganizationFeaturesType = new Set([
  ...businessClassFeaturesVersion3,

  OrganizationFeature.EnterpriseUI,
]);

const businessClassProducts = new Set([
  ProductName.BusinessClass,
  ProductName.BusinessClass2PO,
  ProductName.BusinessClass3PO,
  ProductName.BusinessClass3x1PO,
  ProductName.BusinessClass3x3PO,
]);

export const products: ProductSpecMap = new Map([
  [
    35,
    {
      shortName: ProductShortName.BusinessClassMonthly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Monthly,
      price: 25,
      features: businessClassFeatures,
      hidden: true,
      prebill: false,
      upgrade: true,
      yearlyEquivalent: 36,
    },
  ],
  [
    36,
    {
      shortName: ProductShortName.BusinessClassMonthly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Yearly,
      price: 200,
      features: businessClassFeatures,
      hidden: true,
      upgrade: true,
    },
  ],
  [
    39,
    {
      shortName: ProductShortName.BusinessClassMonthly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Monthly,
      price: 50,
      features: businessClassFeatures,
      hidden: true,
      upgrade: true,
      yearlyEquivalent: 40,
    },
  ],
  [
    40,
    {
      shortName: ProductShortName.BusinessClassYearly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Yearly,
      price: 500,
      features: businessClassFeatures,
      hidden: true,
      upgrade: true,
    },
  ],
  [
    96,
    {
      shortName: ProductShortName.BusinessClassMonthly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Monthly,
      price: 5,
      features: businessClassFeaturesVersion2,
      perUser: true,
      upgrade: true,
      yearlyEquivalent: 97,
    },
  ],
  [
    97,
    {
      shortName: ProductShortName.BusinessClassYearly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Yearly,
      price: 45,
      features: businessClassFeaturesVersion2,
      perUser: true,
      upgrade: true,
    },
  ],
  [
    98,
    {
      shortName: ProductShortName.BusinessClass3Monthly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Monthly,
      price: 10,
      features: businessClassFeaturesVersion3,
      perUser: true,
      perActiveUser: true,
      yearlyEquivalent: 99,
    },
  ],
  [
    99,
    {
      shortName: ProductShortName.BusinessClass3Yearly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Yearly,
      price: 100,
      features: businessClassFeaturesVersion3,
      perUser: true,
      perActiveUser: true,
    },
  ],
  [
    100,
    {
      shortName: ProductShortName.Enterprise,
      name: ProductName.Enterprise,
      interval: ProductInterval.Yearly,
      price: 0,
      features: enterpriseFeatures,
      perUser: false,
      perActiveUser: false,
      hidden: true,
      purchaseOrder: true,
    },
  ],
  [
    101,
    {
      shortName: ProductShortName.BusinessClass2PO,
      name: ProductName.BusinessClass2PO,
      interval: ProductInterval.Yearly,
      price: 0,
      features: businessClassFeaturesVersion2,
      perUser: false,
      perActiveUser: false,
      hidden: true,
      purchaseOrder: true,
      upgrade: true,
    },
  ],
  [
    102,
    {
      shortName: ProductShortName.BusinessClass3PO,
      name: ProductName.BusinessClass3PO,
      interval: ProductInterval.Yearly,
      price: 0,
      features: businessClassFeaturesVersion3,
      perUser: false,
      perActiveUser: false,
      hidden: true,
      purchaseOrder: true,
    },
  ],
  [
    103,
    {
      shortName: ProductShortName.BusinessClass3x1Monthly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Monthly,
      price: 12.5,
      features: businessClassFeaturesVersion3,
      perUser: true,
      perActiveUser: true,
      yearlyEquivalent: 104,
    },
  ],
  [
    104,
    {
      shortName: ProductShortName.BusinessClass3x1Yearly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Yearly,
      price: 119.99,
      features: businessClassFeaturesVersion3,
      perUser: true,
      perActiveUser: true,
    },
  ],
  [
    105,
    {
      shortName: ProductShortName.BusinessClass3x2Monthly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Monthly,
      price: 12.5,
      features: businessClassFeaturesVersion3WithMBG,
      perUser: true,
      perActiveUser: false,
      yearlyEquivalent: 111,
    },
  ],
  [
    106,
    {
      shortName: ProductShortName.BusinessClass3x2Yearly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Yearly,
      price: 119.99,
      features: businessClassFeaturesVersion3WithMBG,
      perUser: true,
      perActiveUser: false,
    },
  ],
  [
    107,
    {
      shortName: ProductShortName.BusinessClass3x1PO,
      name: ProductName.BusinessClass3x1PO,
      interval: ProductInterval.Yearly,
      price: 0,
      features: businessClassFeaturesVersion3WithMBG,
      perUser: false,
      perActiveUser: false,
      hidden: true,
      purchaseOrder: true,
    },
  ],
  [
    108,
    {
      shortName: ProductShortName.Enterprise1x1TieredPricing,
      name: ProductName.Enterprise1x1,
      interval: ProductInterval.Yearly,
      price: 0,
      features: enterpriseFeatures,
      perUser: false,
      perActiveUser: false,
      hidden: true,
      purchaseOrder: true,
    },
  ],
  [
    109,
    {
      shortName: ProductShortName.Enterprise1x2SiteLicense,
      name: ProductName.Enterprise1x2,
      interval: ProductInterval.Yearly,
      price: 0,
      features: enterpriseFeatures,
      perUser: false,
      perActiveUser: false,
      hidden: true,
      purchaseOrder: true,
    },
  ],
  [
    110,
    {
      shortName: ProductShortName.BusinessClass3x3Monthly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Monthly,
      price: 12.5,
      features: businessClassFeaturesVersion3x3,
      perUser: true,
      perActiveUser: false,
      yearlyEquivalent: 111,
    },
  ],
  [
    111,
    {
      shortName: ProductShortName.BusinessClass3x3Yearly,
      name: ProductName.BusinessClass,
      interval: ProductInterval.Yearly,
      price: 119.99,
      features: businessClassFeaturesVersion3x3,
      perUser: true,
      perActiveUser: false,
    },
  ],
  [
    112,
    {
      shortName: ProductShortName.BusinessClass3x3PO,
      name: ProductName.BusinessClass3x3PO,
      interval: ProductInterval.Yearly,
      price: 0,
      features: businessClassFeaturesVersion3x3,
      perUser: false,
      perActiveUser: false,
      hidden: true,
      purchaseOrder: true,
    },
  ],
]);

interface OrgPriceObject {
  totalPerUserPrice(productType: string): number;
  getBillingStrategy(
    productType: string,
  ):
    | {
        perUser: boolean;
        perActiveUser: boolean;
        userCount: number;
      }
    | {
        flatRate: true;
      };
}

class OrganizationFeatureSet extends FeatureSet {
  globalFeatures = new Set([OrganizationFeature.Logo]);

  currentProducts = {
    monthly: 'businessClass3x3Monthly',
    yearly: 'businessClass3x3Yearly',
  };

  BC1Products = [35, 36, 39, 40];

  // Returns the price object for a backbone organization model
  currentPriceObjectForModel = (org: OrgPriceObject) => {
    const monthlyProduct = this.currentProducts.monthly;
    const yearlyProduct = this.currentProducts.yearly;

    const monthlyProductPrice = this.getPrice(monthlyProduct);
    const yearlyProductPrice = this.getPrice(yearlyProduct);

    return {
      monthly: {
        productNumber: this.getProductNumber(monthlyProduct),
        price: monthlyProductPrice,
        totalPrice: org.totalPerUserPrice(monthlyProduct),
        monthlyPrice: monthlyProductPrice,
        totalMonthlyPrice: org.totalPerUserPrice(monthlyProduct),
      },
      yearly: {
        productNumber: this.getProductNumber(yearlyProduct),
        price: yearlyProductPrice,
        totalPrice: org.totalPerUserPrice(yearlyProduct),
        monthlyPrice: yearlyProductPrice && yearlyProductPrice / 12,
        totalMonthlyPrice: org.totalPerUserPrice(yearlyProduct) / 12,
        savePercent: this.getYearlyDiscountPercent(
          yearlyProduct,
          monthlyProduct,
        ),
      },
      billingStrategy: org.getBillingStrategy(yearlyProduct),
    };
  };

  getBillingStrategy = (
    product: ProductDesc,
    activeBillableMemberCount: number,
    billableCollaboratorCount: number,
    billableMemberCount: number,
  ): BillingStrategy => {
    if (this.isPerUser(product)) {
      const isPerActiveUser = this.isPerActiveUser(product) || false;
      return {
        perUser: true,
        perActiveUser: isPerActiveUser,
        billableCollaboratorCount: billableCollaboratorCount,
        userCount: isPerActiveUser
          ? activeBillableMemberCount
          : billableMemberCount,
      };
    } else {
      return {
        flatRate: true,
      };
    }
  };

  totalPerUserPrice = (
    product: ProductDesc,
    billingStrategy: BillingStrategy,
    billingDates: BillingDates,
  ): number => {
    if (
      isFlatRateBillingStrategy(billingStrategy) ||
      !billingStrategy.perUser
    ) {
      return 0;
    }
    const { billableCollaboratorCount, userCount } = billingStrategy;

    const productCode = this.getProductNumber(product);
    const billingDate =
      productCode &&
      billingDates &&
      billingDates[productCode] &&
      new Date(billingDates[productCode]);
    const gracePeriodEndDate = new Date('2018-12-17T08:00:00.000Z');
    const isMultiBoardGuestEnabled =
      productCode &&
      this.isFeatureEnabled(OrganizationFeature.MultiBoardGuests, productCode);
    const isToBeUpgradedToMBGPlanEnabled =
      productCode &&
      this.isFeatureEnabled(
        OrganizationFeature.ToBeUpgradedToMBGPlan,
        productCode,
      );
    const shouldIncludeBillableCollaborators = Boolean(
      isMultiBoardGuestEnabled ||
        (isToBeUpgradedToMBGPlanEnabled &&
          // billingDate may not exist in the case of an organization purchasing a different product
          billingDate &&
          billingDate.getTime() >= gracePeriodEndDate.getTime()),
    );
    const perUserPrice = this.getPrice(product) || 0;

    return shouldIncludeBillableCollaborators
      ? (userCount + billableCollaboratorCount) * perUserPrice
      : userCount * perUserPrice;
  };

  currentPriceObject = (
    activeBillableMemberCount: number,
    billableCollaboratorCount: number,
    billableMemberCount: number,
    billingDates: BillingDates,
  ) => {
    const monthlyProduct = this.currentProducts.monthly;
    const yearlyProduct = this.currentProducts.yearly;

    const monthlyProductPrice = this.getPrice(monthlyProduct)!;
    const yearlyProductPrice = this.getPrice(yearlyProduct)!;

    const billingStrategy = this.getBillingStrategy(
      yearlyProduct,
      activeBillableMemberCount,
      billableCollaboratorCount,
      billableMemberCount,
    );

    const totalMonthlyPerUserPrice = this.totalPerUserPrice(
      monthlyProduct,
      billingStrategy,
      billingDates,
    )!;

    const totalYearlyPerUserPrice = this.totalPerUserPrice(
      yearlyProduct,
      billingStrategy,
      billingDates,
    )!;

    return {
      monthly: {
        productNumber: this.getProductNumber(monthlyProduct)!,
        price: monthlyProductPrice,
        totalPrice: totalMonthlyPerUserPrice,
        monthlyPrice: monthlyProductPrice,
        totalMonthlyPrice: totalMonthlyPerUserPrice,
      },
      yearly: {
        productNumber: this.getProductNumber(yearlyProduct)!,
        price: yearlyProductPrice,
        totalPrice: totalYearlyPerUserPrice,
        monthlyPrice: yearlyProductPrice && yearlyProductPrice / 12,
        totalMonthlyPrice: totalYearlyPerUserPrice / 12,
        savePercent: this.getYearlyDiscountPercent(
          yearlyProduct,
          monthlyProduct,
        )!,
      },
      billingStrategy,
    };
  };

  isBusinessClassProduct = (productNumber?: number) => {
    if (!productNumber) {
      return false;
    }

    const productName = this.products.get(productNumber);
    if (!productName) {
      return false;
    }

    return businessClassProducts.has(productName);
  };

  constructor(organizationProducts: ProductSpecMap) {
    super(organizationProducts);
  }
}

export const OrganizationFeatures = new OrganizationFeatureSet(products);
